/**
 * Phone-shaped status glyph.
 *
 * A coloured dot said "something is a colour"; a phone outline says *what* is green. Colour alone is
 * never the whole message — each icon carries a title and an aria-label spelling the state out,
 * because a red dot does not tell a tester to go and check the cable.
 */

import type { DeviceStatus } from '../api/types'

export function PhoneIcon({
  status,
  title,
  size = 22,
}: {
  status: DeviceStatus
  title: string
  size?: number
}) {
  const color =
    status === 'online' ? 'var(--ok)' : status === 'busy' ? 'var(--warn)' : 'var(--bad)'
  return (
    <span className="phone-icon" style={{ color }} title={title} role="img" aria-label={title}>
      <svg width={size} height={size * 1.5} viewBox="0 0 16 24" aria-hidden focusable="false">
        <rect
          x="1"
          y="1"
          width="14"
          height="22"
          rx="3"
          fill="currentColor"
          fillOpacity="0.16"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <rect x="5.5" y="3" width="5" height="1.2" rx="0.6" fill="currentColor" />
        {status === 'busy' ? (
          // two "in use" bars: busy is not a failure, it is someone working
          <g fill="currentColor">
            <rect x="5" y="10" width="2" height="6" rx="1" />
            <rect x="9" y="10" width="2" height="6" rx="1" />
          </g>
        ) : status === 'offline' ? (
          // a cross: nothing is coming from this phone until someone looks at it
          <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <line x1="5.5" y1="10" x2="10.5" y2="15" />
            <line x1="10.5" y1="10" x2="5.5" y2="15" />
          </g>
        ) : (
          <path
            d="M5 12.6l2 2 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  )
}
